/** "Retry job creation" releases a quarantined Visit back to the Job stamper: record surfaces and board bulk. */
import { computed, inject, Injector } from '@angular/core';
import { ActivityEntityName, JobSecurityRight, VisitBrowseCommandId, VisitBrowseRow, visitBrowseCapabilities } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import type { BulkCommandArgs, BulkCommandOutcome, EntityBoardBulkCommandsArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { ActionCommandCapability } from '@workbuddy/ui/v2/surface.engine';
import { visitSurfaceModel } from '../visit.model';

const LABEL = 'Retry job creation';
const ICON = 'fa-regular fa-arrows-rotate';
const QUEUED = 'Job creation queued';
export const VISIT_RETRY_JOB_CREATION_BULK_ID = 'visit.retryJobCreation';
/** One person releasing a page of quarantined Visits; there is no server bulk action for a retry, so each is its own command. */
const MAX_VISIT_RETRY_BULK = 50;
// A few in flight keeps a full batch quick without flooding the API.
const RETRY_CONCURRENCY = 4;

type RetryVisits = (rows: readonly VisitBrowseRow[]) => Promise<boolean>;

export function visitRetryJobCreationCommand(hostReadonly = false): ActionCommandCapability<VisitBrowseRow> {

  const rights = inject(RightsService);
  const data = inject(EntityDataService);
  // Resolved at run time: the surface factory mounts in hosts that never retry, so the toast outlet is not a mount dependency.
  const injector = inject(Injector);
  // Not gated on the page's readonly: a quarantined Visit is often locked for editing, yet retrying writes none of its fields.
  const allowed = (row: VisitBrowseRow) => !hostReadonly && retryable(row) && canRetry(rights);
  return {
    descriptor: ({ page }) => computed(() => ({ id: 'retryJobCreation', label: LABEL, iconLeft: ICON, pin: 'preferred', hidden: !allowed(page.$model()), disabled: page.$saving() || page.$dirty() })),
    run: async page => {
      const row = page.$model();
      if (!allowed(row) || page.$saving() || page.$dirty()) return;
      const updated = await retryJobCreation(data, row);
      if (!updated?._id) throw new Error('The Visit command did not return the updated Visit');
      page.reset({ ...visitSurfaceModel(updated) });
      injector.get(ToastService).success(LABEL, QUEUED);
      data.invalidate(ActivityEntityName.Visit);
      await data.invalidateItems(ActivityEntityName.UpcomingWork);
    },
  };
}

export function visitRetryJobCreationBulkCommands(args: EntityBoardBulkCommandsArgs<VisitBrowseRow>): Command[] {

  const rows = (args.selectedItems ?? []).filter(retryable);
  if (!withinCap(rows) || !canRetry(inject(RightsService))) return [];
  return [new Command({ _id: VISIT_RETRY_JOB_CREATION_BULK_ID, label: LABEL, iconLeft: ICON, data: { run: boardRetrier() } satisfies { run: RetryVisits } })];
}

export async function runVisitRetryJobCreationBulk(args: BulkCommandArgs<VisitBrowseRow>): Promise<BulkCommandOutcome | false> {

  const run = (args.commandData as { run?: RetryVisits } | undefined)?.run;
  if (args.action !== VISIT_RETRY_JOB_CREATION_BULK_ID || !run) return false;
  const rows = args.data.filter(retryable);
  if (!withinCap(rows)) return { handled: true, clearSelection: false };
  return { handled: true, clearSelection: await run(rows) };
}

function retryable(row: VisitBrowseRow): boolean {

  return visitBrowseCapabilities(row).retryJobCreation;
}

/** Mirrors the strategy: queuing a Job build is a dispatch decision, not only a Visit edit. */
function canRetry(rights: RightsService): boolean {

  return rights.hasRights(JobSecurityRight.CanAccess) && rights.hasRights(JobSecurityRight.Manage) && rights.hasRights(JobSecurityRight.Dispatch);
}

function withinCap(rows: readonly VisitBrowseRow[]): boolean {

  return rows.length > 0 && rows.length <= MAX_VISIT_RETRY_BULK;
}

function retryJobCreation(data: EntityDataService, row: VisitBrowseRow): Promise<VisitBrowseRow> {

  return data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VisitBrowseCommandId.RetryJobCreation, { id: row.visitId ?? row._id });
}

/** Stamped in the board's injection context; the bulk handler runs outside it. Resolves true when any Visit was queued. */
function boardRetrier(): RetryVisits {

  const injector = inject(Injector);
  return async rows => {
    const data = injector.get(EntityDataService);
    const toasts = injector.get(ToastService);
    const failures: string[] = [];
    const queue = [...rows];
    const worker = async () => {
      for (let row = queue.shift(); row; row = queue.shift()) {
        try {
          await retryJobCreation(data, row);
        } catch (error) {
          failures.push(error instanceof Error ? error.message : 'The visit could not be updated.');
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(RETRY_CONCURRENCY, rows.length) }, worker));
    const queued = rows.length - failures.length;
    if (queued) {
      data.invalidate(ActivityEntityName.Visit);
      await data.invalidateItems(ActivityEntityName.UpcomingWork);
    }
    const plural = (count: number) => count === 1 ? '1 visit' : `${count} visits`;
    if (!failures.length) toasts.success(LABEL, rows.length === 1 ? QUEUED : `${QUEUED} for ${plural(queued)}`);
    else if (!queued) toasts.error(LABEL, `None of the selected visits could be updated: ${failures[0]}`);
    else toasts.warning(LABEL, `${QUEUED} for ${queued} of ${plural(rows.length)} ${failures.length} could not be updated: ${failures[0]}`);
    return queued > 0;
  };
}
